/**
 * App Catalogue — Codes.
 *
 * Pick an app to work with its redemption-code pool. Only apps whose distribution method
 * actually hands out codes appear here — an Android install link has no pool, so listing
 * it would be a dead end.
 *
 * Route: /admin/app-catalogue/codes
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconRefresh, IconDeviceMobile, IconMoodEmpty } from '@tabler/icons-react';
import {
  Stack,
  Group,
  Button,
  Text,
  Title,
  Alert,
  Paper,
  Loader,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { useApplicationCatalogueService, useAppDistributionService } from '@actschurch/ccms-sdk';
import type { AppDistributionView } from '@actschurch/ccms-sdk';
import { ADMIN_ROUTES } from '@/config/routes';
import AppBrowser, { type AppBrowserItem } from './components/AppBrowser';

const APPS_KEY = ['admin', 'app-catalogue', 'all'];
const DISTRIBUTIONS_KEY = ['admin', 'app-catalogue', 'distributions'];

export default function CodesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const catalogueService = useApplicationCatalogueService();
  const distributionService = useAppDistributionService();

  const { data: apps = [], isLoading: appsLoading } = useQuery({
    queryKey: APPS_KEY,
    queryFn: () => catalogueService.getAllApps(),
  });

  const {
    data: distributions = [],
    isLoading: distributionsLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: DISTRIBUTIONS_KEY,
    queryFn: () => distributionService.getDistributions(),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: DISTRIBUTIONS_KEY });
  };

  // Only apps that actually draw from a pool.
  const items: AppBrowserItem[] = useMemo(() => {
    const withPool = distributions.filter((d) => d.yieldsCode);
    return apps
      .filter((app) => withPool.some((d) => d.appId === app.appId))
      .map((app) => {
        const pool = withPool.find((d) => d.appId === app.appId)!;
        return {
          app,
          summary: summarise(pool),
          badge: pool.stats?.isLowStock
            ? { label: 'Low on codes', color: 'red' }
            : { label: pool.platformName, color: 'violet' },
        };
      });
  }, [apps, distributions]);

  if (appsLoading || distributionsLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Codes</Title>
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap="lg">
        <Title order={2}>Codes</Title>
        <Alert color="red" title="Can't reach the distribution service">
          <Text size="sm">
            {error instanceof Error ? error.message : 'The Application Catalogue did not respond.'}
          </Text>
          <Text size="sm" mt="xs">
            If this is a fresh deploy: migration 194 creates the code tables, and the
            <strong> ccms-api-application-catalogue</strong> container needs redeploying.
          </Text>
          <Button variant="light" color="red" size="xs" mt="sm" onClick={refresh}>
            Retry
          </Button>
        </Alert>
      </Stack>
    );
  }

  return (
    <FadeIn>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Codes</Title>
            <Text c="dimmed" size="sm">
              Redemption-code pools. Pick an app to upload codes, hand them out, and see which
              were used.
            </Text>
          </div>
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconRefresh size={16} />}
            onClick={refresh}
            loading={isFetching}
          >
            Refresh
          </Button>
        </Group>

        {items.length === 0 ? (
          <Paper withBorder p="xl" ta="center">
            <Stack gap="sm" align="center">
              <IconMoodEmpty size={32} color="var(--mantine-color-gray-5)" />
              <Text c="dimmed" maw={480}>
                No app uses codes yet. On the Distribution page, set an app's iOS method to{' '}
                <strong>Apple Redemption Code</strong> — that is the one that draws from a pool.
              </Text>
              {/* Don't dead-end: go straight to the page that unblocks this. */}
              <Button
                leftSection={<IconDeviceMobile size={16} />}
                onClick={() => navigate(ADMIN_ROUTES.APP_CATALOGUE_DISTRIBUTION)}
              >
                Set up distribution
              </Button>
              <Text size="xs" c="dimmed" maw={480}>
                Android has no code pool: Google Play doesn't issue promo codes for free apps, so
                Android users install from a link.
              </Text>
            </Stack>
          </Paper>
        ) : (
          <AppBrowser
            items={items}
            onSelect={(appId) => navigate(`/admin/app-catalogue/codes/${appId}`)}
            emptyMessage="No apps match your search."
          />
        )}
      </Stack>
    </FadeIn>
  );
}

function summarise(pool: AppDistributionView): string {
  const s = pool.stats;
  if (!s) return `${pool.platformName} codes`;
  return `${s.available} available · ${s.assigned} assigned · ${s.redeemed} used`;
}
